import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { KeyRound, Dice5, Save } from "lucide-react";
import { PLATFORM_STAFF_ROLES, generatePassword } from "./utils";

export default function EditStaffDialog({ open, onOpenChange, staff, onDone, canChangeRole }) {
  const [f, setF] = useState({ name: "", email: "", role: "platform_staff" });
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (staff) setF({ name: staff.name || "", email: staff.email || "", role: staff.role });
    setPw("");
  }, [staff]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name: f.name, email: f.email.trim().toLowerCase() };
      if (canChangeRole) body.role = f.role;
      await api.patch(`/superadmin/users/${staff.user_id}`, body);
      toast.success("Staff updated");
      onDone();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (pw.length < 6) { toast.error("At least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/superadmin/users/${staff.user_id}/reset-password`, { new_password: pw });
      toast.success("Password reset — share it with them securely.");
      setPw("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (!staff) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit platform staff</DialogTitle>
          <DialogDescription>Update details, change role, or reset password.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Name</label>
            <input data-testid="edit-staff-name" required className="input-soft" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Email</label>
            <input data-testid="edit-staff-email" required type="email" className="input-soft" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          </div>
          {canChangeRole && (
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Role</label>
              <div className="space-y-2">
                {PLATFORM_STAFF_ROLES.map((r) => (
                  <button key={r.key} type="button" onClick={() => setF({ ...f, role: r.key })}
                    data-testid={`edit-staff-role-${r.key}`}
                    className={`w-full text-left px-4 py-3 rounded-2xl border transition-all ${f.role === r.key ? "border-[#E07A5F] bg-[#FFF6F1]" : "border-[#EBE5D9] bg-white hover:border-[#E07A5F]/40"}`}>
                    <div className="font-semibold text-[#2C2A29]">{r.label}</div>
                    <div className="text-xs text-[#7A7571]">{r.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <button data-testid="edit-staff-save" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2">
            <Save size={16} /> {busy ? "Saving…" : "Save changes"}
          </button>
        </form>

        <div className="card-soft !p-4 !bg-[#F7F4EC] space-y-3 mt-2">
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Reset password</div>
          <div className="flex gap-2">
            <input data-testid="edit-staff-newpw" type="text" className="input-soft flex-1 font-mono"
              value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 6)" />
            <button type="button" onClick={() => setPw(generatePassword(12))} data-testid="edit-staff-genpw"
              className="btn-secondary !py-2 !px-3 inline-flex items-center gap-1 flex-shrink-0"><Dice5 size={16} /></button>
          </div>
          <button onClick={reset} data-testid="edit-staff-reset" disabled={busy || !pw}
            className="btn-primary w-full inline-flex items-center justify-center gap-2">
            <KeyRound size={14} /> {busy ? "Working…" : "Reset password"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
